import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, IconButton, Typography } from "@mui/material";
import { JSONViewer } from "components/UI/JSONViewer";
import { EmissionRecord } from "models/Emissions";
import React from "react";
import theme from "Theme/AppTheme";

const titles: Record<EmissionRecord["recordType"], string> = {
    Receipt: "Receipt",
    Failure: "Failure",
    RulesFilterEvaluation: "Rule Filter Evaluation",
};

const formatDateTime = (value?: string | null) =>
    value
        ? new Date(value).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "medium",
          })
        : "–";

const display = (value: unknown) => {
    if (value === null || value === undefined || value === "") return "–";
    return typeof value === "string" ? value : JSON.stringify(value);
};

const getFields = (
    record: EmissionRecord,
    configurationName: string,
): [string, string][] => {
    const common: [string, string][] = [
        ["Conversation Id", record.audioMetadataId],
        ["Configuration", configurationName],
    ];

    if (record.receipt) {
        return [
            ...common,
            ["Attempt", display(record.receipt.attempt)],
            ["Emitted On", formatDateTime(record.receipt.emittedOn)],
            ["Emission Reason", display(record.receipt.emissionReason)],
            ["Receipt Id", record.receipt.id],
        ];
    }

    if (record.failure) {
        return [
            ...common,
            ["Total Attempts", display(record.failure.totalAttempts)],
            ["Failed At", formatDateTime(record.failure.failedAt)],
            ["Endpoint URI", display(record.failure.endpointUri)],
        ];
    }

    if (record.rulesFilterEvaluation) {
        return [
            ...common,
            ["Passed", record.rulesFilterEvaluation.passed ? "Yes" : "No"],
            ["Rule Count", display(record.rulesFilterEvaluation.ruleCount)],
            [
                "Evaluated At",
                formatDateTime(record.rulesFilterEvaluation.evaluatedAt),
            ],
        ];
    }

    return common;
};

const codeBlockSx = {
    margin: 0,
    backgroundColor: theme.palette.white.main,
    borderRadius: 0,
    padding: "10px 12px",
    fontSize: "11px",
    lineHeight: 1.7,
    color: theme.palette.gray[700],
    // JSONViewer renders a pre-wrap block, so long lines wrap and only height overflows.
    overflow: "auto",
    maxHeight: 260,
};

// Matches the header treatment EmissionsJsonField uses on the configuration forms, so the
// read-only panel and the editable forms present long values the same way.
const DetailCard: React.FC<{
    label: string;
    badge: string;
    children: React.ReactNode;
}> = ({ label, badge, children }) => (
    <Box
        sx={{
            border: `1px solid ${theme.palette.gray[200]}`,
            borderRadius: "8px",
            overflow: "hidden",
        }}
    >
        <Box
            sx={{
                backgroundColor: theme.palette.gray[50],
                borderBottom: `1px solid ${theme.palette.gray[200]}`,
                padding: "8px 12px",
                fontSize: "11px",
                color: theme.palette.gray.main,
                display: "flex",
                justifyContent: "space-between",
            }}
        >
            <span>{label}</span>
            <span>{badge}</span>
        </Box>
        {children}
    </Box>
);

const JsonCard: React.FC<{ label: string; value: string }> = ({
    label,
    value,
}) => (
    <DetailCard label={label} badge="JSON">
        <JSONViewer value={value} sx={codeBlockSx} />
    </DetailCard>
);

// Errors arrive as full stack traces. Left aligned and scrolled in place, so the first line —
// the part that says what went wrong — is readable without the row growing without limit.
const ErrorCard: React.FC<{ value: string }> = ({ value }) => (
    <DetailCard label="Error Message" badge="text">
        <Box
            component="pre"
            sx={{
                ...codeBlockSx,
                textAlign: "left",
                // Wrap rather than scroll sideways, matching how JSONViewer renders raw text.
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                fontFamily: "monospace",
            }}
        >
            {value}
        </Box>
    </DetailCard>
);

type Props = {
    record: EmissionRecord;
    configurationName: string;
    /**
     * The lookup that produced this record could not read all of the conversation's older rule
     * filter evaluations. It describes that lookup, not missing fields on the record below.
     */
    incomplete?: boolean;
    onClose: () => void;
};

const EmissionRecordDetailPanel: React.FC<Props> = ({
    record,
    configurationName,
    incomplete,
    onClose,
}) => {
    const evaluation = record.rulesFilterEvaluation;

    return (
        <Box
            component="aside"
            aria-label={`${titles[record.recordType]} detail`}
            sx={{
                backgroundColor: theme.palette.white.main,
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: "10px",
                boxShadow: "0 8px 24px rgba(16,24,40,.08)",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                minWidth: 0,
                alignSelf: "start",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
                    {titles[record.recordType]}
                </Typography>
                <IconButton
                    aria-label="Close record detail"
                    size="small"
                    onClick={onClose}
                    sx={{ marginLeft: "auto" }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {incomplete && (
                <Alert severity="warning" sx={{ fontSize: "12px" }}>
                    Some older rule filter evaluations for this conversation
                    couldn’t be loaded.
                </Alert>
            )}

            <Box>
                {getFields(record, configurationName).map(([label, value]) => (
                    <Box
                        key={label}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "12px",
                            fontSize: "12px",
                            padding: "8px 0",
                            borderBottom: `1px solid ${theme.palette.gray[100]}`,
                            "&:last-of-type": { borderBottom: "none" },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                color: theme.palette.gray.main,
                                flexShrink: 0,
                            }}
                        >
                            {label}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                color: theme.palette.gray[700],
                                fontWeight: 600,
                                textAlign: "right",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {value}
                        </Box>
                    </Box>
                ))}
            </Box>

            {record.failure?.errorMessage && (
                <ErrorCard value={record.failure.errorMessage} />
            )}

            {evaluation && (
                <>
                    <JsonCard
                        label="Diagnostics"
                        value={evaluation.diagnosticsJson ?? ""}
                    />
                    <JsonCard
                        label="Rule Map"
                        value={evaluation.ruleMapJson ?? ""}
                    />
                </>
            )}
        </Box>
    );
};

export default EmissionRecordDetailPanel;
